/**
 * ChatInput - Message input with AI-powered responses
 */

import { useState, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { useChatStore } from '@/stores';
import { processMessage } from '@/services/chat/blurpAI';
import { cn } from '@/utils/cn';

export function ChatInput() {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const addMessage = useChatStore((state) => state.addMessage);
  const isSubmitting = useChatStore((state) => state.isSubmitting);
  const setSubmitting = useChatStore((state) => state.setSubmitting);
  const setTyping = useChatStore((state) => state.setTyping);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();

    const trimmed = value.trim();
    if (!trimmed || isSubmitting) return;

    // Add user message
    addMessage({
      type: 'user',
      content: trimmed,
    });

    // Clear input
    setValue('');
    setSubmitting(true);
    setTyping(true);

    try {
      // Get AI response
      const response = await processMessage(trimmed);

      // Add AI response as assistant message
      addMessage({
        type: 'assistant',
        content: response,
      });
    } catch (error) {
      console.error('Chat error:', error);
      addMessage({
        type: 'assistant',
        content: "I'm having trouble processing that request. Please try again.",
      });
    } finally {
      setSubmitting(false);
      setTyping(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Quick suggestion chips
  const suggestions = [
    'Air quality?',
    'Safe route?',
    'Neural Index?',
    'Go outside?',
  ];

  return (
    <div className="space-y-2">
      {/* Quick suggestions (show when input is empty) */}
      {!value && !isSubmitting && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setValue(suggestion)}
              className={cn(
                'px-2.5 py-1 text-xs rounded-full',
                'bg-hud-primary/10 border border-hud-primary/30',
                'text-hud-primary hover:bg-hud-primary/20',
                'transition-colors'
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="relative">
        <div
          className={cn(
            'relative flex items-end gap-2 rounded-lg',
            'bg-bg-surface border transition-colors',
            isFocused ? 'border-hud-primary' : 'border-glass-border'
          )}
        >
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Ask Blurp.ie anything..."
            disabled={isSubmitting}
            rows={1}
            className={cn(
              'flex-1 bg-transparent px-3 py-2.5',
              'text-sm text-text-primary placeholder:text-text-muted',
              'resize-none outline-none',
              'max-h-[120px] min-h-[40px]'
            )}
            style={{
              height: 'auto',
              overflowY: value.split('\n').length > 3 ? 'auto' : 'hidden',
            }}
          />

          <button
            type="submit"
            disabled={!value.trim() || isSubmitting}
            className={cn(
              'flex-shrink-0 w-10 h-10 m-1 rounded',
              'flex items-center justify-center',
              'transition-all duration-200',
              value.trim() && !isSubmitting
                ? 'bg-hud-primary/20 text-hud-primary hover:bg-hud-primary/30'
                : 'bg-transparent text-text-muted cursor-not-allowed'
            )}
          >
            {isSubmitting ? (
              <LoadingIcon className="w-5 h-5 animate-spin" />
            ) : (
              <SendIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Character counter */}
        {value.length > 200 && (
          <div className="absolute -bottom-5 right-0 text-[10px] text-text-muted">
            {value.length}/500
          </div>
        )}

        {/* Typing indicator */}
        <TypingIndicator />
      </form>
    </div>
  );
}

function TypingIndicator() {
  const isTyping = useChatStore((state) => state.isTyping);

  if (!isTyping) return null;

  return (
    <div className="flex items-center gap-2 mt-2 text-xs text-text-secondary">
      <div className="flex gap-1">
        <span className="w-1.5 h-1.5 bg-hud-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 bg-hud-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 bg-hud-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>Blurp.ie is thinking...</span>
    </div>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M12 2v4m0 12v4m-8-10h4m12 0h4m-3.636-6.364l-2.828 2.828M6.464 17.536l-2.828 2.828m12.728 0l-2.828-2.828M6.464 6.464L3.636 3.636" />
    </svg>
  );
}

export default ChatInput;
